import { useState, useEffect, useRef } from 'react';
import { useBanAutocomplete } from '../../hooks/useBanAutocomplete';
import { BanDropdown } from './BanDropdown';
import { Modal } from './Modal';
import { ConfirmDialog } from './ConfirmDialog';
import { Input } from './Input';
import { Button } from './Button';
import { ContactForm, type ContactFormData } from './ContactForm';
import { MapPin, Plus, X, UserPlus, ChevronDown, Search } from 'lucide-react';
import { CountrySelect } from './CountrySelect';
import clsx from 'clsx';
import { contactsAPI, bandsAPI, venuesAPI } from '../../api/endpoints';
import type { Address, Contact, Venue, VenueNameMatch, VenueVisibility, MonthPeriod, Month, Band } from '../../types';
import { cleanAddress, emptyAddress } from '../../utils/addressFormat';
import {
  type VenueFormData,
  venueTypes,
  statusOptions,
  interestLevelOptions,
  musicStyles,
  MONTHS,
  emptyMonthPeriod,
  buildInterestsForForm,
  inferVenueTypeFromName,
  DEFAULT_VENUE_TYPE,
  COORD_INVALIDATING_FIELDS,
} from './venueConstants';

interface VenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (venueData: VenueFormData) => void;
  onDelete?: (id: string) => void;
  onVisibilityChange?: () => void;
  venue?: Venue | null;
  mode?: 'create' | 'edit';
  readonlyCoreFields?: boolean;
  /**
   * Create mode only: the user picked an existing venue in the name field's suggestions instead
   * of creating a duplicate. The caller is expected to switch the modal to edit mode on it.
   */
  onSelectExistingVenue?: (venue: Venue) => void;
}

/** Name-field suggestions (create mode): how many existing venues to offer, and the typing pause. */
const NAME_SUGGESTION_LIMIT = 5;
const NAME_SEARCH_DEBOUNCE_MS = 250;
const NAME_SEARCH_MIN_CHARS = 2;

const initialFormData: VenueFormData = {
  name: '',
  url: '',
  type: DEFAULT_VENUE_TYPE,
  commentary: '',
  address: emptyAddress(),
  coordinates: null,
  styles: [],
  previous_artists: [],
  interests: [],
  status: 'open',
  contact_ids: [],
  ideal_contact_period: emptyMonthPeriod(),
  expect_answer_by: emptyMonthPeriod(),
  concert_period: emptyMonthPeriod(),
};

export function VenueModal({ isOpen, onClose, onSubmit, onDelete, onVisibilityChange, venue, mode = 'create', readonlyCoreFields = false, onSelectExistingVenue }: VenueModalProps) {
  const [formData, setFormData] = useState<VenueFormData>(initialFormData);
  const [bands, setBands] = useState<Band[]>([]);
  const [newStyle, setNewStyle] = useState('');
  const [newArtist, setNewArtist] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPublicConfirm, setShowPublicConfirm] = useState(false);
  const [isRequestingPublic, setIsRequestingPublic] = useState(false);
  const [error, setError] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  // Contacts created here as gatekeepers: the role is the venue's, sent with it on submit
  const [newGatekeeperIds, setNewGatekeeperIds] = useState<string[]>([]);
  const [contactError, setContactError] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const [banHighlightedIdx, setBanHighlightedIdx] = useState(-1);
  // Existing venues matching the name being typed (create mode) — see nameSuggestionsOpen below
  const [nameSuggestions, setNameSuggestions] = useState<VenueNameMatch[]>([]);
  const [nameSuggestionsDismissed, setNameSuggestionsDismissed] = useState(false);
  // The suggestion being fetched in full before the caller switches the modal to edit mode
  const [pickedSuggestionId, setPickedSuggestionId] = useState<string | null>(null);
  const nameFieldRef = useRef<HTMLDivElement>(null);
  const nameSearchAbortRef = useRef<AbortController>(undefined);
  // Set once the user picks a type themselves: name-based inference stops guessing after that
  const typePickedManuallyRef = useRef(false);

  const { suggestions: banSuggestions, activeField: banActiveField, isLoading: banLoading, triggerSearch, selectSuggestion, dismiss: dismissBan } =
    useBanAutocomplete((updates, coordinates) =>
      setFormData(prev => ({ ...prev, address: { ...prev.address, ...updates }, coordinates }))
    );

  // Close type dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
        setTypeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load bands when modal opens
  useEffect(() => {
    if (isOpen) {
      bandsAPI.list().then(res => setBands(res.data.items)).catch(() => setBands([]));
    }
  }, [isOpen]);

  /* --- Name field: existing-venue suggestions (create mode) ---------------------------------
   * Typing a name searches the user's venues so an existing one can be opened for editing
   * instead of being duplicated. It calls the name-only lookup, not the venue list: `?q=` there
   * also covers commentary and contact names, so it joins notes and contacts for the whole
   * account before it matches anything — far too slow to run while someone types. */
  const canSuggestExisting = mode === 'create' && !readonlyCoreFields && !!onSelectExistingVenue;
  const nameQuery = formData.name.trim();

  useEffect(() => {
    if (!isOpen || !canSuggestExisting || nameQuery.length < NAME_SEARCH_MIN_CHARS) {
      setNameSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      nameSearchAbortRef.current?.abort();
      const controller = new AbortController();
      nameSearchAbortRef.current = controller;
      venuesAPI
        .nameLookup(nameQuery, controller.signal)
        .then(res => {
          if (!controller.signal.aborted) setNameSuggestions(res.data.slice(0, NAME_SUGGESTION_LIMIT));
        })
        .catch(() => {
          // Aborted or failed: no suggestions, the name stays whatever was typed
        });
    }, NAME_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isOpen, canSuggestExisting, nameQuery]);

  // Drop any in-flight search when the modal closes
  useEffect(() => {
    if (!isOpen) nameSearchAbortRef.current?.abort();
  }, [isOpen]);

  const nameSuggestionsOpen = canSuggestExisting && !nameSuggestionsDismissed && nameSuggestions.length > 0;

  // Clicking outside the name field dismisses the suggestions until the name changes again
  useEffect(() => {
    if (!nameSuggestionsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (nameFieldRef.current && !nameFieldRef.current.contains(e.target as Node)) {
        setNameSuggestionsDismissed(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [nameSuggestionsOpen]);

  // Escape closes the suggestions first (capture phase, so Modal's own handler doesn't close it)
  useEffect(() => {
    if (!nameSuggestionsOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setNameSuggestionsDismissed(true);
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [nameSuggestionsOpen]);

  /** A suggestion was picked: fetch the real venue (the lookup only returns name + city) and hand
   *  it to the caller, which switches this modal to edit mode on it. */
  const handlePickSuggestion = async (match: VenueNameMatch) => {
    if (!onSelectExistingVenue || pickedSuggestionId) return;
    setPickedSuggestionId(match._id);
    try {
      const res = await venuesAPI.getById(match._id);
      onSelectExistingVenue(res.data);
    } catch {
      setError(`Could not open "${match.name}"`);
    } finally {
      setPickedSuggestionId(null);
    }
  };

  // Initialize form when modal opens or venue changes
  useEffect(() => {
    if (isOpen) {
      if (venue && mode === 'edit') {
        setFormData({
          name: venue.name || '',
          url: venue.url || '',
          type: venue.type,
          commentary: venue.commentary || '',
          address: venue.address || emptyAddress(),
          coordinates: venue.coordinates ?? null,
          styles: venue.styles || [],
          previous_artists: venue.previous_artists || [],
          interests: buildInterestsForForm(venue.interests || [], bands),
          status: venue.status || 'open',
          contact_ids: venue.contact_ids || [],
          ideal_contact_period: venue.ideal_contact_period || emptyMonthPeriod(),
          expect_answer_by: venue.expect_answer_by || emptyMonthPeriod(),
          concert_period: venue.concert_period || emptyMonthPeriod(),
        });
        // Load existing contacts for the venue
        loadVenueContacts(venue.contact_ids || []);
      } else {
        // Create mode - reset to initial with band interests
        setFormData({ ...initialFormData, interests: buildInterestsForForm([], bands) });
        setSelectedContacts([]);
      }
      setShowContactForm(false);
      setNewGatekeeperIds([]);
      setContactError('');
      setError('');
      setNewStyle('');
      setShowDeleteConfirm(false);
      setShowPublicConfirm(false);
      setNameSuggestions([]);
      setNameSuggestionsDismissed(false);
      setPickedSuggestionId(null);
      typePickedManuallyRef.current = false;
    }
  }, [isOpen, venue, mode, bands]);

  /** Typing or pasting a name the type can be inferred from ("fest" → Festival, "comite" →
   *  Comité des fêtes) pre-fills that type (create mode only, and never over a type the user
   *  picked). Clearing the telltale word hands the default type back. */
  const handleNameChange = (name: string) => {
    setNameSuggestionsDismissed(false);
    setFormData(prev => {
      if (mode !== 'create' || typePickedManuallyRef.current) {
        return { ...prev, name };
      }
      const inferred = inferVenueTypeFromName(name);
      const type = inferred ?? (inferVenueTypeFromName(prev.name) ? DEFAULT_VENUE_TYPE : prev.type);
      return { ...prev, name, type };
    });
  };

  const loadVenueContacts = async (contactIds: string[]) => {
    if (contactIds.length === 0) {
      setSelectedContacts([]);
      return;
    }
    try {
      // Fetched by id, not filtered out of a page of the account's contacts: that page held the
      // first hundred, so a venue's contacts were almost never among them. Identity fields only —
      // the list below shows a name and an email or phone.
      const response = await contactsAPI.batchGet(contactIds, { summary: true });
      setSelectedContacts(response.data);
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setSelectedContacts([]);
    }
  };

  const handleSubmit = async () => {
    setError('');

    if (!formData.name.trim()) {
      setError('Venue name is required');
      return;
    }

    // Clean address and interests before submitting
    const cleanedInterests = formData.interests.filter(
      i => i.level !== 'no_information' || (i.comment && i.comment.trim())
    );
    const submitData = readonlyCoreFields
      ? {
          // Only send notes fields when core is locked
          commentary: formData.commentary,
          interests: cleanedInterests,
          ideal_contact_period: formData.ideal_contact_period,
          expect_answer_by: formData.expect_answer_by,
          contact_ids: formData.contact_ids,
        } as VenueFormData
      : {
          ...formData,
          address: cleanAddress(formData.address),
          interests: cleanedInterests,
        };
    if (newGatekeeperIds.length > 0) {
      // Only when roles were added here, so roles changed elsewhere meanwhile are not overwritten
      submitData.gatekeeper_contact_ids = [...new Set([...(venue?.gatekeeper_contact_ids ?? []), ...newGatekeeperIds])];
    }

    onSubmit(submitData);
    resetAndClose();
  };

  const resetAndClose = () => {
    setFormData(initialFormData);
    setSelectedContacts([]);
    setNewStyle('');
    setError('');
    setShowContactForm(false);
    setContactError('');
    onClose();
  };

  const handleDelete = () => {
    if (!venue || !onDelete) return;
    onDelete(venue._id);
    resetAndClose();
  };

  const handleRequestPublic = async () => {
    if (!venue) return;
    try {
      setIsRequestingPublic(true);
      await venuesAPI.requestPublic(venue._id);
      onVisibilityChange?.();
      resetAndClose();
    } catch (err: any) {  // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.detail || 'Failed to request public visibility');
    } finally {
      setIsRequestingPublic(false);
    }
  };

  const visibilityConfig: Record<VenueVisibility, { label: string; disabled: boolean }> = {
    private: { label: 'Make Public', disabled: false },
    pending_public: { label: 'Public Request Made', disabled: true },
    refused_public: { label: 'Cannot Be Made Public', disabled: true },
    public: { label: 'Public', disabled: true },
  };

  const handleAddStyle = (style: string) => {
    if (style && !formData.styles.includes(style)) {
      setFormData({ ...formData, styles: [...formData.styles, style] });
      setNewStyle('');
    }
  };

  const handleRemoveStyle = (style: string) => {
    setFormData({
      ...formData,
      styles: formData.styles.filter((s) => s !== style),
    });
  };

  const handleAddArtist = (artist: string) => {
    if (artist && !formData.previous_artists.includes(artist)) {
      setFormData({ ...formData, previous_artists: [...formData.previous_artists, artist] });
      setNewArtist('');
    }
  };

  const handleRemoveArtist = (artist: string) => {
    setFormData({
      ...formData,
      previous_artists: formData.previous_artists.filter((a) => a !== artist),
    });
  };

  const handleCreateContact = async (data: ContactFormData) => {
    const isGatekeeper = data.flags === 'gatekeeper';
    const payload: Record<string, unknown> = {
      name: data.name.trim(),
      is_generic: data.is_generic,
      // Gatekeeper is this venue's role, sent with the venue — not a status of the contact
      flags: isGatekeeper ? 'none' : data.flags,
    };

    if (data.email.trim()) payload.email = data.email.trim();
    if (data.phone.trim()) payload.phone = data.phone.trim();
    if (data.function.trim()) payload.function = data.function.trim();
    if (data.commentary.trim()) payload.commentary = data.commentary.trim();

    // An email the user already has a contact for brings that contact back (`reused`)
    const response = await contactsAPI.create(payload);
    const newContact = response.data;
    if (isGatekeeper) setNewGatekeeperIds(prev => [...prev, newContact._id]);

    // Add to local state
    const updatedContacts = [...selectedContacts.filter(c => c._id !== newContact._id), newContact];
    setSelectedContacts(updatedContacts);

    // Update formData with new contact ID
    const newContactIds = updatedContacts.map(c => c._id);
    setFormData(prev => ({
      ...prev,
      contact_ids: newContactIds,
    }));

    setShowContactForm(false);
  };

  const handleRemoveContact = (contactId: string) => {
    const newSelected = selectedContacts.filter(c => c._id !== contactId);
    setSelectedContacts(newSelected);
    setNewGatekeeperIds(prev => prev.filter(id => id !== contactId));
    setFormData(prev => ({
      ...prev,
      contact_ids: newSelected.map(c => c._id),
    }));
  };

  const updateAddressField = (field: keyof Address, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value || undefined,
      },
      // Typing a location by hand (rather than picking a BAN suggestion) invalidates any
      // stored pin — coordinates are only ever re-established by a suggestion or the backfill.
      ...(COORD_INVALIDATING_FIELDS.has(field) ? { coordinates: null } : {}),
    }));
  };

  const BAN_TRIGGER_FIELDS = ['city', 'zip_code', 'administrative_region_1', 'administrative_region_2'] as const;

  const handleAddressFieldChange = (field: keyof Address, value: string) => {
    updateAddressField(field, value);
    if ((BAN_TRIGGER_FIELDS as readonly string[]).includes(field)) {
      // The BAN only knows French addresses — outside France there is nothing to suggest.
      triggerSearch(field as typeof BAN_TRIGGER_FIELDS[number], value, formData.address?.country);
    } else {
      dismissBan();
    }
  };

  // Reset highlight when suggestion list changes
  useEffect(() => {
    setBanHighlightedIdx(-1);
  }, [banSuggestions]);

  const updateMonthPeriod = (
    field: 'ideal_contact_period' | 'expect_answer_by' | 'concert_period',
    update: Partial<MonthPeriod>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] || emptyMonthPeriod()),
        ...update,
      },
    }));
  };

  const toggleMonth = (
    field: 'ideal_contact_period' | 'expect_answer_by' | 'concert_period',
    month: Month
  ) => {
    const current = formData[field] || emptyMonthPeriod();
    const months = current.months.includes(month)
      ? current.months.filter(m => m !== month)
      : [...current.months, month];
    updateMonthPeriod(field, { months, all_year: false });
  };

  const toggleAllYear = (
    field: 'ideal_contact_period' | 'expect_answer_by' | 'concert_period'
  ) => {
    const current = formData[field] || emptyMonthPeriod();
    updateMonthPeriod(field, { all_year: !current.all_year, months: [] });
  };

  const renderMonthPeriodSelector = (
    field: 'ideal_contact_period' | 'expect_answer_by' | 'concert_period',
    label: string,
    disabled = false
  ) => {
    const period = formData[field] || emptyMonthPeriod();
    return (
      <div className={clsx(disabled && "opacity-50 pointer-events-none")}>
        <label className="block text-xs font-medium text-dark-text-secondary mb-1">
          {label}
        </label>
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => toggleAllYear(field)}
            className={clsx(
              'px-2 py-0.5 rounded-sm text-xs font-medium border',
              period.all_year
                ? 'border-primary-600 bg-primary-900/30 text-primary-400'
                : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
            )}
          >
            All
          </button>
          {MONTHS.map(({ value, short }) => (
            <button
              key={value}
              type="button"
              onClick={() => toggleMonth(field, value)}
              disabled={period.all_year}
              className={clsx(
                'px-1.5 py-0.5 rounded-sm text-xs font-medium border',
                period.all_year
                  ? 'border-dark-border bg-dark-bg text-dark-text-muted cursor-not-allowed'
                  : period.months.includes(value)
                  ? 'border-primary-600 bg-primary-900/30 text-primary-400'
                  : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              {short}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={mode === 'edit' ? 'Edit Venue' : 'Add New Venue'}
      size="4xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button
            type="button"
            variant="ghost"
            onClick={resetAndClose}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            {showContactForm && (
              <span className="text-xs text-amber-400">
                Finish or cancel contact form first
              </span>
            )}
            {mode === 'edit' && onDelete && !readonlyCoreFields && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                >
                Delete Venue
              </Button>
            )}
            {mode === 'edit' && venue && !readonlyCoreFields && (() => {
              const vis = visibilityConfig[venue.visibility || 'private'];
              return (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowPublicConfirm(true)}
                  disabled={vis.disabled}
                >
                  {vis.label}
                </Button>
              );
            })()}
            <Button
              type="button"
              variant="primary"
              disabled={showContactForm}
              onClick={handleSubmit}
            >
              {mode === 'edit' ? 'Update Venue' : 'Create Venue'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {readonlyCoreFields && (
          <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg text-blue-400 text-sm">
            <span className="font-medium">{formData.name}</span> is a public venue. You can only edit personal notes.
          </div>
        )}

        {!readonlyCoreFields && (
          <>
          {/* Row 1: Name | Status (dropdown) | Type */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-3">
            <div className="relative" ref={nameFieldRef}>
              <Input
                label="Venue Name *"
                type="text"
                placeholder="e.g., The Blue Note"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                autoComplete="off"
                required
              />
              {nameSuggestionsOpen && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-dark-elevated border border-dark-border rounded-lg shadow-lg overflow-hidden">
                  <div className="px-3 py-1.5 text-xs text-dark-text-muted border-b border-dark-border">
                    Already in your venues — select to edit
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {nameSuggestions.map((v) => (
                      <button
                        key={v._id}
                        type="button"
                        onClick={() => handlePickSuggestion(v)}
                        disabled={pickedSuggestionId !== null}
                        className={clsx(
                          'w-full flex items-baseline gap-2 text-left px-3 py-2 text-sm text-dark-text-secondary hover:bg-dark-border',
                          pickedSuggestionId !== null && 'opacity-60',
                          pickedSuggestionId === v._id && 'bg-dark-border',
                        )}
                      >
                        <span className="truncate">{v.name}</span>
                        {v.city && <span className="text-xs text-dark-text-muted shrink-0">{v.city}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-dark-text-secondary mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as VenueFormData['status'] })}
                className="input"
              >
                {statusOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div ref={typeDropdownRef} className="relative min-w-[160px]">
              <label className="block text-sm font-medium text-dark-text-secondary mb-1">
                Type *
              </label>
              <button
                type="button"
                onClick={() => {
                  setTypeDropdownOpen(!typeDropdownOpen);
                  setTypeSearch('');
                }}
                className="w-full flex items-center justify-between px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-sm font-medium text-dark-text-primary hover:bg-dark-border focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              >
                <span>{venueTypes.find(t => t.value === formData.type)?.label || 'Select type'}</span>
                <ChevronDown className={clsx('w-4 h-4 ml-2 text-dark-text-secondary transition-transform', typeDropdownOpen && 'rotate-180')} />
              </button>
              {typeDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-dark-elevated border border-dark-border rounded-lg shadow-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-dark-border">
                    <Search className="w-3.5 h-3.5 text-dark-text-muted" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={typeSearch}
                      onChange={(e) => setTypeSearch(e.target.value)}
                      className="w-full bg-transparent text-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {venueTypes
                      .filter(({ label }) => label.toLowerCase().includes(typeSearch.toLowerCase()))
                      .map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            typePickedManuallyRef.current = true;
                            setFormData({ ...formData, type: value });
                            setTypeDropdownOpen(false);
                            setTypeSearch('');
                          }}
                          className={clsx(
                            'w-full text-left px-3 py-2 text-sm',
                            formData.type === value
                              ? 'bg-primary-900/30 text-primary-400'
                              : 'text-dark-text-secondary hover:bg-dark-border'
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    {venueTypes.filter(({ label }) => label.toLowerCase().includes(typeSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-dark-text-muted text-center">
                        No matching types
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: URL */}
          <Input
            label="Website URL"
            type="url"
            placeholder="https://venue-website.com"
            value={formData.url || ''}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />

          {/* Row 3: Music Styles | Previous Artists */}
          <div className="border-t border-dark-border pt-3 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Music Styles
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  placeholder="Custom style..."
                  value={newStyle}
                  onChange={(e) => setNewStyle(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleAddStyle(newStyle)}
                  disabled={!newStyle.trim()}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </Button>
              </div>
              {formData.styles.filter(s => !musicStyles.includes(s)).length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {formData.styles.filter(s => !musicStyles.includes(s)).map((style) => (
                    <span
                      key={style}
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium"
                    >
                      {style}
                      <button type="button" onClick={() => handleRemoveStyle(style)}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {musicStyles.map((style) => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => handleAddStyle(style)}
                    disabled={formData.styles.includes(style)}
                    className={clsx(
                      'px-2.5 py-1 rounded-full text-xs font-medium',
                      formData.styles.includes(style)
                        ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                    )}
                  >
                    {formData.styles.includes(style) ? '✓ ' : '+ '}
                    {style}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Previous Artists
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  placeholder="Add artist..."
                  value={newArtist}
                  onChange={(e) => setNewArtist(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleAddArtist(newArtist)}
                  disabled={!newArtist.trim()}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </Button>
              </div>
              {formData.previous_artists.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {formData.previous_artists.map((artist) => (
                    <span
                      key={artist}
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium"
                    >
                      {artist}
                      <button type="button" onClick={() => handleRemoveArtist(artist)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Address Section */}
          <div className="border-t border-dark-border pt-3">
            <label className="block text-sm font-medium text-dark-text-secondary mb-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              Address
            </label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                label="Street"
                placeholder="123 Main St"
                value={formData.address?.street || ''}
                onChange={(e) => updateAddressField('street', e.target.value)}
              />
              {/* City — BAN autocomplete trigger */}
              <div className="relative">
                <Input
                  label="City"
                  placeholder="Paris"
                  value={formData.address?.city || ''}
                  onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'city') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'city' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectSuggestion} />
                )}
              </div>
              {/* Postal Code — BAN autocomplete trigger */}
              <div className="relative">
                <Input
                  label="Postal Code"
                  placeholder="75001"
                  value={formData.address?.zip_code || ''}
                  onChange={(e) => handleAddressFieldChange('zip_code', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'zip_code') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'zip_code' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectSuggestion} />
                )}
              </div>
              {/* Dept/County — BAN autocomplete trigger */}
              <div className="relative">
                <Input
                  label="Dept/County"
                  placeholder="Seine"
                  value={formData.address?.administrative_region_1 || ''}
                  onChange={(e) => handleAddressFieldChange('administrative_region_1', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'administrative_region_1') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'administrative_region_1' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectSuggestion} />
                )}
              </div>
              {/* Region/State — BAN autocomplete trigger */}
              <div className="relative">
                <Input
                  label="Region/State"
                  placeholder="Ile-de-France"
                  value={formData.address?.administrative_region_2 || ''}
                  onChange={(e) => handleAddressFieldChange('administrative_region_2', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'administrative_region_2') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'administrative_region_2' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectSuggestion} />
                )}
              </div>
              <CountrySelect
                label="Country"
                value={formData.address?.country || ''}
                onChange={(val) => handleAddressFieldChange('country', val)}
              />
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium text-dark-text-secondary mb-1">
                Full Address
              </label>
              <div className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text-muted text-sm min-h-[38px]">
                {formData.address?.unparsed_address || '—'}
              </div>
            </div>
          </div>
          </>
        )}

        {/* Commentary */}
        <div className="border-t border-dark-border pt-3">
          <label className="block text-sm font-medium text-dark-text-secondary mb-1">
            Commentary
          </label>
          <textarea
            placeholder="Notes about this venue..."
            value={formData.commentary || ''}
            onChange={(e) => setFormData({ ...formData, commentary: e.target.value })}
            className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden focus:ring-2 focus:ring-primary-500 min-h-[60px] text-sm"
          />
        </div>

        {/* Interest Levels (per band) */}
        <div className="border-t border-dark-border pt-3">
          <label className="block text-sm font-medium text-dark-text-secondary mb-2">
            Interest Levels
          </label>
          <div className="space-y-2">
            {formData.interests.map((interest, idx) => {
              const bandLabel = interest.band_id === null
                ? 'Overall'
                : bands.find(b => b._id === interest.band_id)?.name || interest.band_id;
              return (
                <div key={interest.band_id ?? 'overall'} className="flex items-center gap-2">
                  <span className="text-xs font-medium text-dark-text-primary min-w-[70px] shrink-0">
                    {bandLabel}
                  </span>
                  <div className="flex gap-1">
                    {interestLevelOptions.map(({ value, short }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          const updated = [...formData.interests];
                          updated[idx] = { ...updated[idx], level: value };
                          setFormData({ ...formData, interests: updated });
                        }}
                        className={clsx(
                          'px-1.5 py-0.5 rounded-sm text-xs font-medium border',
                          interest.level === value
                            ? 'border-primary-600 bg-primary-900/30 text-primary-400'
                            : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                        )}
                      >
                        {short}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Comment..."
                    value={interest.comment || ''}
                    onChange={(e) => {
                      const updated = [...formData.interests];
                      updated[idx] = { ...updated[idx], comment: e.target.value || undefined };
                      setFormData({ ...formData, interests: updated });
                    }}
                    className="flex-1 px-2 py-0.5 bg-dark-elevated border border-dark-border rounded-sm text-xs text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Time Periods Section */}
        <div className="border-t border-dark-border pt-3 space-y-2">
          <label className="block text-sm font-medium text-dark-text-secondary">
            Scheduling Periods
          </label>
          {renderMonthPeriodSelector('ideal_contact_period', 'Ideal Contact Period')}
          {renderMonthPeriodSelector('expect_answer_by', 'Expect Answer By')}
          {!readonlyCoreFields && renderMonthPeriodSelector('concert_period', 'Concert Period')}
        </div>

        {/* Contacts Section */}
        <div className="border-t border-dark-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-dark-text-secondary">Contacts</label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowContactForm(!showContactForm)}
            >
              <UserPlus className="w-3.5 h-3.5" />
              {showContactForm ? 'Cancel' : 'New Contact'}
            </Button>
          </div>

          {/* Selected contacts list */}
          {selectedContacts.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {selectedContacts.map(contact => (
                <div
                  key={contact._id}
                  className="flex items-center justify-between px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-dark-text-primary">{contact.name}</span>
                    <span className="text-xs text-dark-text-muted">{contact.email || contact.phone || ''}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveContact(contact._id)}
                    className="p-0.5 hover:bg-dark-border rounded-sm"
                  >
                    <X className="w-3.5 h-3.5 text-dark-text-secondary hover:text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* New contact form */}
          {showContactForm && (
            <div className="bg-dark-bg border border-dark-border rounded-lg p-3">
              <ContactForm
                mode="create"
                onSubmit={handleCreateContact}
                onCancel={() => setShowContactForm(false)}
                error={contactError}
                venueScoped
              />
            </div>
          )}

          {!showContactForm && selectedContacts.length === 0 && (
            <div className="text-xs text-dark-text-muted text-center py-3 bg-dark-elevated/30 rounded-lg border border-dashed border-dark-border">
              No contacts added yet.
            </div>
          )}
        </div>
      </div>
    </Modal>
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Venue"
        message="Are you sure you want to delete this venue? This action cannot be undone."
        confirmLabel="Delete Venue"
        danger
      />
      <ConfirmDialog
        isOpen={showPublicConfirm}
        onClose={() => setShowPublicConfirm(false)}
        onConfirm={handleRequestPublic}
        title="Make Venue Public"
        message="This will submit a request to make this venue visible to all users. An admin will review your request. Continue?"
        confirmLabel="Request Public"
        isLoading={isRequestingPublic}
      />
    </>
  );
}
